import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { Link as RouterLink } from 'react-router-dom';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import {
    DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext, rectSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { AuthService } from '../../../services/auth.service';
import { UserType } from '../../../interfaces/userType.enum';
import { isWidgetVisibleToRole } from '../registry';
import { ROLE_PRESETS } from '../registry/presets';
import { isGatedModule } from '../registry/moduleMeta';
import { useDashboardAccess } from '../useDashboardAccess';
import useDashboardLayout from './useDashboardLayout';
import ActionCenter from './ActionCenter';
import BoardKpiStrip from './BoardKpiStrip';
import WidgetCard from './WidgetCard';
import AddWidgetDialog from './AddWidgetDialog';

const SPAN: Record<string, number> = { sm: 4, md: 6, lg: 12 };

function SortableWidget({ id, children }: { id: string; children: React.ReactNode }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    return (
        <Box
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            sx={{ height: '100%' }}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
        >
            {children}
        </Box>
    );
}

export default function DashboardBoard() {
    const { t } = useTranslation('dashboard');
    const { user } = useAuth();
    const roles: UserType[] = useMemo(() => {
        const r = user?.roles;
        return (Array.isArray(r) ? r : r ? [r] : []) as UserType[];
    }, [user]);
    const institutionId = AuthService.getSelectedInstitution();

    // Each user sees only their own board: the primary preset their role (or grants) gives them.
    // There are no role-preview tabs, so the board is always the user's own persisted, editable layout.
    const { primary, showUpgradeHint, enabledModules } = useDashboardAccess();

    const { order, widgets, add, remove, reorder, reset } = useDashboardLayout({
        institutionId, boardKey: 'home', fallbackIds: ROLE_PRESETS[primary], editable: true,
    });
    // A widget whose module the institution has since disabled must disappear rather than hang on
    // a permanent spinner (its fetch would 403 and the widget swallows that error). `order`/`widgets`
    // (and their persisted layout) keep the hidden entry so it reappears if the module is re-enabled;
    // only rendering + the drag surface are filtered. Ungated modules are never in `enabledModules`,
    // so they must not be filtered out — see isGatedModule. A saved widget the user's role no longer
    // allows is hidden the same way.
    const visibleWidgets = useMemo(
        () => widgets.filter(def =>
            (!isGatedModule(def.module) || enabledModules.has(def.module))
            && isWidgetVisibleToRole(def, roles)),
        [widgets, enabledModules, roles],
    );
    const [addOpen, setAddOpen] = useState(false);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const onDragEnd = (e: DragEndEvent) => {
        if (!e.over || e.active.id === e.over.id) return;
        const from = order.indexOf(String(e.active.id));
        const to = order.indexOf(String(e.over.id));
        if (from >= 0 && to >= 0) reorder(from, to);
    };

    return (
        <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>{t('board.addWidget')}</Button>
                <Button size="small" startIcon={<RestartAltIcon />} onClick={reset}>{t('board.resetToDefault')}</Button>
                {(roles.includes(UserType.InstitutionAdmin) || roles.includes(UserType.InstitutionManager)) && (
                    <Button size="small" startIcon={<TrackChangesIcon />} component={RouterLink} to="/dashboard/settings/targets">
                        {t('board.kpiTargets')}
                    </Button>
                )}
            </Stack>

            {showUpgradeHint && (
                <Alert
                    icon={<WorkspacePremiumIcon fontSize="inherit" />}
                    severity="info"
                    sx={{ mb: 2 }}
                >
                    <Trans t={t} i18nKey="board.upgradeHint" components={{ strong: <strong /> }} />
                </Alert>
            )}

            <BoardKpiStrip institutionId={institutionId} scope="home" />

            <ActionCenter institutionId={institutionId} />

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={visibleWidgets.map(w => w.id)} strategy={rectSortingStrategy}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                        {visibleWidgets.map(def => (
                            <Box key={def.id} sx={{ gridColumn: { xs: 'span 12', md: `span ${SPAN[def.size]}` } }}>
                                <SortableWidget id={def.id}>
                                    <WidgetCard def={def} institutionId={institutionId} editable onRemove={() => remove(def.id)} />
                                </SortableWidget>
                            </Box>
                        ))}
                    </Box>
                </SortableContext>
            </DndContext>

            <AddWidgetDialog
                open={addOpen}
                roles={roles}
                present={order}
                enabledModules={Array.from(enabledModules)}
                onAdd={add}
                onClose={() => setAddOpen(false)}
            />
        </Box>
    );
}
